import React from 'react';
import { Phone, Mail, MapPin, Clock, Star } from 'lucide-react';
import { BUSINESS, NEIGHBORHOODS, SERVICES } from '@/lib/businessData';

export default function Footer() {
  return (
    <footer className="bg-background border-t-2 border-primary">
      {/* Emergency hotline bar */}
      <div className="bg-primary text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-wider opacity-80">24/7 Emergency Hotline</div>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="font-heading font-black text-3xl sm:text-4xl tracking-tight hover:opacity-80 transition-opacity"
            >
              {BUSINESS.phone}
            </a>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 fill-background" />
            <span className="font-heading font-bold text-lg">{BUSINESS.rating} / 5.0</span>
            <span className="font-mono text-sm opacity-80">— {BUSINESS.reviewCount} Google Reviews</span>
          </div>
        </div>
      </div>

      {/* Technical summary */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-10">
          {/* Company */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-primary flex items-center justify-center font-heading font-black text-background text-lg stencil">
                M
              </div>
              <div>
                <div className="font-heading font-bold text-sm uppercase stencil">MIKE'S</div>
                <div className="font-mono text-[10px] text-muted-foreground">GARAGE DOOR REPAIR</div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Cypress TX's 24/7 emergency garage door repair specialists. {BUSINESS.yearsInBusiness} years of local,
              honest, precision service for every neighborhood in the Cypress area.
            </p>
            <div className="font-mono text-xs text-muted-foreground space-y-1">
              <div>License: {BUSINESS.license}</div>
              <div>Hours: {BUSINESS.hours}</div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-bold text-sm uppercase stencil text-primary mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><a href="/blog" className="text-sm text-muted-foreground hover:text-primary transition-colors">Garage Door Blog</a></li>
              <li><a href="#services" className="text-sm text-muted-foreground hover:text-primary transition-colors">Repair Services</a></li>
              <li><a href="/service-areas" className="text-sm text-muted-foreground hover:text-primary transition-colors">Service Areas</a></li>
              <li><a href="/about-mike" className="text-sm text-muted-foreground hover:text-primary transition-colors">About Mike</a></li>
              <li><a href="/testimonials" className="text-sm text-muted-foreground hover:text-primary transition-colors">Customer Reviews</a></li>
              <li><a href="/contact" className="text-sm text-muted-foreground hover:text-primary transition-colors">Contact Us</a></li>
              <li><a href="#faq" className="text-sm text-muted-foreground hover:text-primary transition-colors">FAQ</a></li>
              <li><a href="#booking" className="text-sm text-muted-foreground hover:text-primary transition-colors">Book Service</a></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-heading font-bold text-sm uppercase stencil text-primary mb-4">Repair Services</h3>
            <ul className="space-y-2">
              {SERVICES.map((service) => (
                <li key={service.id}>
                  <a href="#services" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {service.title}
                  </a>
                </li>
              ))}
              <li>
                <a href="#services" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Remote & Keypad Programming
                </a>
              </li>
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h3 className="font-heading font-bold text-sm uppercase stencil text-primary mb-4">Service Areas</h3>
            <ul className="space-y-2">
              {NEIGHBORHOODS.map((hood) => (
                <li key={hood} className="text-sm text-muted-foreground">
                  {hood}, Cypress TX
                </li>
              ))}
              <li className="text-sm text-muted-foreground pt-2 border-t border-border mt-2">
                Also: Katy · Spring · Tomball · Houston
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading font-bold text-sm uppercase stencil text-primary mb-4">Contact</h3>
            <ul className="space-y-3">
              <li>
                <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-start gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
                  <Phone className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  {BUSINESS.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${BUSINESS.email}`} className="flex items-start gap-2 text-sm text-muted-foreground hover:text-primary transition-colors break-all">
                  <Mail className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  {BUSINESS.email}
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                {BUSINESS.address}
              </li>
              <li className="flex items-start gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                {BUSINESS.hours}
              </li>
            </ul>
          </div>
        </div>

        {/* SEO content block */}
        <div className="mt-16 pt-8 border-t border-border">
          <div className="font-mono text-xs text-muted-foreground leading-relaxed max-w-4xl">
            <strong className="text-muted-foreground">Garage Door Repair Cypress TX</strong> — Mike's Garage Door Repair
            is Cypress, Texas's premier 24/7 emergency garage door repair company. We specialize in torsion and extension
            spring replacement, garage door opener repair and installation (LiftMaster, Chamberlain, Genie, Craftsman),
            off-track door realignment, safety sensor repair, cable replacement, and sectional panel installation. Serving
            all Cypress neighborhoods including Bridgeland, Towne Lake, Cypress Creek, Coles Crossing, Fairfield, Cypress
            Lakes, Blackhorse Ranch, and Stonegate — plus Katy, Spring, Tomball, and Greater Houston. Licensed, insured,
            and locally owned for over {BUSINESS.yearsInBusiness} years. When you search "garage door repair near me" in
            Cypress TX, Mike's is the name to trust.
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-mono text-xs text-muted-foreground">
            © {new Date().getFullYear()} {BUSINESS.name}. All rights reserved. {BUSINESS.license}
          </p>
          <div className="flex gap-6">
            <a href="#top" className="font-mono text-xs text-muted-foreground hover:text-primary transition-colors">Back to Top</a>
            <a href="#booking" className="font-mono text-xs text-muted-foreground hover:text-primary transition-colors">Book Service</a>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="font-mono text-xs text-muted-foreground hover:text-primary transition-colors">Call Now</a>
          </div>
        </div>
      </div>
    </footer>
  );
}